<template>
    <div>
        <v-data-table
            :headers="headers"
            :items="mapItems(this.indicatorBooks)"
            :sort-by="['academic_period', 'generated_at']"
            :sort-desc="[true, true]"
            multi-sort
            class="table"
            :class="{'table-disabled': flags.loading_bibles}"
            hide-default-footer
            :loading="flags.loading_bibles"
            :loading-text="$t('indicators.indicators_list.loading')"
            :search="search"
            :page.sync="pagination.page"
            :items-per-page="pagination.itemsPerPage"
            :page-count="pageCount"
        >
            <template #no-data>
                <span>{{ $t('indicators.indicators_list.no_records_found') }}</span>
            </template>
            <template #item.status="{ item }">
                <v-chip
                    :color="getReportStatusBadgeColor(item.status)"
                    text-color="white"
                    class="px-0"
                    label
                    small
                >
                    <span class="text-center status-chip">{{ getReportStatusTranslation(item.status) }}</span>
                </v-chip>
            </template>
            <template #item.actions="{ item }">
                <template v-if="item.status === 'IN_REVIEW'">
                    <v-btn
                            class='btn-icon--primary btn-icon'
                            @click="editBibleDialog=true"
                        ><v-icon>mdi-pencil</v-icon>
                    </v-btn>
                    <v-menu offset-y>
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn
                                    class='btn-icon--primary btn-icon'
                                    v-bind="attrs"
                                    v-on="on"
                                ><v-icon>mdi-dots-vertical</v-icon>
                            </v-btn>
                        </template>
                        <v-list>
                            <v-list-item @click="downloadBibleDialog=true" class="menu-change-state-color">
                                <v-icon class="menu-change-state-color">mdi-sync</v-icon>{{ $t('indicators.status_change') }}
                            </v-list-item>
                            <v-list-item @click="showDeleteConfirmationDialogFor(item)" class="menu-delete-color">
                                <v-icon class="menu-delete-color">mdi-delete</v-icon>{{ $t('indicators.delete') }}
                            </v-list-item>
                        </v-list>
                    </v-menu>
                </template>
                <template v-else>
                    <div class="action-buttons">
                        <v-btn
                               v-if="checkPermission('INDICATORS_EDIT')"
                               :disabled="!(item.status === 'OPEN' && item.academic_period_current === 1)"
                               class='btn-icon--primary btn-icon'
                               @click='editIndicator(item.uuid)'
                        >
                            <v-icon>mdi-pencil</v-icon>
                        </v-btn>

                        <v-btn
                                v-if="checkPermission('INDICATORS_DOWNLOAD')"
                                class='btn-icon--primary btn-icon'
                                @click='downloadIndicators(item.uuid, item.title)'
                            ><v-icon>mdi-download</v-icon>
                        </v-btn>
                        <v-btn
                                v-if="checkPermission('INDICATOR_VIEW')"
                                class='btn-icon--primary btn-icon'
                                @click='watchIndicators(item.uuid)'
                            ><v-icon>mdi-eye</v-icon>
                        </v-btn>
                    </div>
                </template>
            </template>
        </v-data-table>

        <delete-bible-dialog
            :showDialog="deleteBibleDialog"
            :bible="bibleToDelete"
            @cancelBibleDeletion="hideBibleDeletionDialog"
            @confirmBibleDeletion="removeBible"
        />

        <download-bible-dialog
            :showDialog="downloadBibleDialog"
            :bible="bibleToDownload"
            @cancelBibleDownload="hideBibleDownloadDialog"
            @confirmBibleDownload="downloadBible"
        />

        <edit-bible-dialog
            :showDialog="editBibleDialog"
            :bible="indicatorBookToEdit"
            @cancelBibleEdition="hideBibleEditionDialog"
            @confirmBibleEdition="editBible"
        />

        <div class="text-center pt-2">
            <v-pagination
                v-model="pagination.page"
                class="pagination"
                :length="pageCount"
                :total-visible="pagination.totalVisible"
                :disabled="flags.loading_bibles"
                @input="onPaginationChange"
            ></v-pagination>
        </div>
    </div>
</template>

<script>
import DeleteBibleDialog from "./DeleteBibleDialog";
import DownloadBibleDialog from "./DownloadBibleDialog";
import EditBibleDialog from "./EditBibleDialog";
import {EventBus} from "@/event-bus";
import {mapState, mapActions} from 'pinia';
import {IndicatorsStore} from "@/Store/Indicators/IndicatorsStore";
import toastMixin from "@/Shared/toastMixin";
import {i18n} from "@/i18n";
import _ from "lodash";
import {PermissionsStore} from "@/Store/PermissionsStore";

export default {
    name: "IndicatorBooksTable",

    components: {
        DeleteBibleDialog,
        DownloadBibleDialog,
        EditBibleDialog,
    },

    props: {
        type: {
            type: Object,
            required: true,
        },
        search: {
            type: String,
            required: true,
        }
    },

    mixins: [toastMixin],

    data () {
        return {
            filters: {
                indicator_type: this.type.id,
                academic_periods: [],
                statuses: [],
            },

            flags: {
                loading_bibles: false,
            },

            headers: [
                { text: i18n.t('indicators.indicators_list.headers.title'),
                    align: 'start',
                    value: 'title'
                },
                { text: i18n.t('indicators.indicators_list.headers.academic_period'),
                    value: 'academic_period'
                },
                { text: i18n.t('indicators.indicators_list.headers.generated_at'),
                    value: 'generated_at'
                },
                { text: i18n.t('indicators.indicators_list.headers.generated_by'),
                    value: 'generated_by'
                },
                { text: i18n.t('indicators.indicators_list.headers.status'),
                    value: 'status'
                },
                { align: 'center',
                    value:'actions',
                    sortable: false
                }
            ],

            pagination: {
                page: 1,
                itemsPerPage: 12,
                totalVisible: 12,
            },

            deleteBibleDialog: false,
            downloadBibleDialog: false,
            editBibleDialog: false,
            cancelDownload: null,
            editingBible: false,
            cancelEdit: null,
            bibleToDelete: {},
            bibleToDownload: {},
            indicatorBookToEdit: {},
            indicatorBookToWatch: {},
        }
    },

    computed: {
        pageCount() {
           // return Math.ceil(this.indicatorBooks.length / this.pagination.itemsPerPage);
            return Math.ceil(1 / this.pagination.itemsPerPage);
        },

        ...mapState(IndicatorsStore, ['indicatorBooks']),
    },

    methods: {

        /**
         * Get Indicator Report
         *
         * @returns {Promise<void>}
         */
        async downloadBible() {
            try {
                this.cancelDownload = new AbortController();
                const response = await axios.get(route('indicators.download', { 'type': this.type.id, indicatorBookId: this.bibleToDownload.indicatorBookId }), {
                    responseType: 'blob',
                    signal: this.cancelDownload.signal,
                });

                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;

                const filename = this.excelName.title + '.xlsx';
                link.setAttribute('download', filename);

                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);

                this.toast().success(
                    this.$t('indicators.indicators_list.success_download_title'),
                );

                this.downloadBibleDialog = false;
            } catch (error) {
                if(error.name === 'CanceledError'){
                    this.toast().error(
                        this.$t('indicators.indicators_list.cancel_download_message'),
                    );
                } else {
                    this.toast().error(
                        this.$t('indicators.indicators_list.error_download_message'),
                    );
                }
            }
        },

        mapItems(object){
            let result = [];
            for (const [key, value] of Object.entries(object)) {
                let entries  = Object.entries(value)
                let item = [];
                for (const [key_, value_] of entries) {
                    item[key_] = value_;
                }
                result.push(Object.assign({}, item));
            }
            return result;
        },

        /**
         * Set filters for searching indicators
         * based on criteria
         *
         * @param filters
         */
        applyFilters(filters) {
            this.filters = Object.assign(this.filters, filters);

            this.pagination.page = 1;

            //this.fetchReports();
        },

        /**
         * Gets reports on pagination change
         */
        onPaginationChange() {
            //this.fetchReports();
        },

        /**
         * Search reports based on filters
         */
        async fetchReports() {
            /*try {
                this.flags.loading_bibles = true;

                let response = await this.searchReports({
                    filters: _.cloneDeep(this.filters),
                    pagination:  _.cloneDeep(this.pagination)
                });

                this.reports = _.get(response, 'data._embedded.reports')

                this.pagination.page = _.get(response, 'data.pagination.current_page');
                this.pagination.itemsLength = _.get(response, 'data.pagination.total');
                this.pagination.pageCount = _.get(response, 'data.pagination.total_pages');

            } catch (ex) {

                this.toast().error(
                    this.$t('indicators.indicators_list.error_search_title'),
                    this.$t('indicators.indicators_list.error_search_message'),
                );

            } finally {

                this.flags.loading_bibles = false;
            }*/
        },

        /**
         *
         * @param status
         * @returns {*}
         */
        getReportStatusBadgeColor(status) {
            let colorsByStatus = {
                GENERATED: 'success',
                IN_REVIEW: 'warning',
                FAILED: 'error',
                OPEN: 'hsla(201, 100%, 41%, 1)',
                CLOSED: 'hsla(217, 11%, 40%, 1)'
            }

            return colorsByStatus[status] || '';
        },

        /**
         *
         * @param status
         * @returns {*}
         */
        getReportStatusTranslation(status) {
            //return i18n.t(`reports.statuses.${status}`);
            return status;
        },

        async editBible(indicatorBookId) {

            if (this.cancelEdit) {
                this.cancelEdit.abort();
            }

            this.cancelEdit = new AbortController();

            try {
                const response = await axios.get(route('indicators.edit', {
                    'type': this.type.id,
                    indicatorBookId: this.indicatorBookToEdit.indicatorBookId
                }), {
                    signal: this.cancelEdit.signal
                });


                window.location.href = response.request.responseURL;

            } catch (error) {
                if (axios.isCancel(error)) {
                    this.toast().error(
                        this.$t('indicators.indicators_list.cancel_edit_message'),
                    );
                    this.editingBible = false;
                    this.cancelEdit = true;
                } else {
                    this.toast().error(
                        this.$t('indicators.indicators_list.error_edit_message'),
                    );
                    console.error('Error editing indicator:', error);
                }
            } finally {
                this.cancelEdit = null;
            }
        },

        viewBible(indicatorBookId) {
            this.$inertia.get(route('indicators.view', { 'type': this.type.id, indicatorBookId: this.indicatorBookToWatch.indicatorBookId }));
        },


        /**
         *
         * @param indicatorBookId
         */
        watchIndicators(indicatorBookId) {
            this.indicatorBookToWatch = {indicatorBookId};
            this.viewBible(indicatorBookId);
        },

        editIndicator(indicatorBookId) {
            this.indicatorBookToEdit = {indicatorBookId};
            this.editBibleDialog=true
        },

        downloadIndicators(indicatorBookId, title) {
            this.bibleToDownload = {indicatorBookId};
            this.excelName = {title};
            this.downloadBibleDialog=true;
        },

        /**
         * Shows dialog to delete given report
         */
        showDeleteConfirmationDialogFor(bible) {
            this.bibleToDelete = _.cloneDeep(bible);

            this.deleteBibleDialog = true;
        },

        /**
         *
         */
        hideBibleDeletionDialog() {
            this.bibleToDelete = {}

            this.deleteBibleDialog = false
        },

        /**
         *
         */
        hideBibleDownloadDialog() {
            if (this.cancelDownload) {
                this.cancelDownload.abort();
                this.downloadBibleDialog = false;
            }
            this.downloadBibleDialog = false;
        },

        hideBibleEditionDialog() {
            if (this.cancelEdit) {
                this.cancelEdit.abort();
                this.editBibleDialog = false;
            }
            this.editBibleDialog = false
        },

        hideBibleViewDialog() {
            this.watchBibleIndicatorsDialog = false;
        },

        /**
         * Deletes selected report
         *
         */
        async removeBible() {
            /*try {

                await this.deleteReport(_.cloneDeep(this.bibleToDelete));

                this.toast().success(
                    this.$t('indicators.indicators_list.success_delete_title', {reportTitle: this.bibleToDelete.title}),
                );

            } catch (ex) {
                console.log(ex);

                this.toast().error(
                    this.$t('indicators.indicators_list.error_delete_title'),
                    this.$t('indicators.indicators_list.error_delete_message'),
                );

            } finally {

                EventBus.$emit('reports.management.delete-report');*/

                this.hideBibleDeletionDialog();

                //await this.fetchReports();
            //}
        },

        /**
         *
         */
        ...mapActions(IndicatorsStore, [
            'getAllIndicatorBooks',
            'searchIndicators',
            'deleteIndicator'
        ]),

        ...mapActions(PermissionsStore, ['checkPermission']),
    },

    created() {
        EventBus.$on('indicators.management.filter-indicators', this.applyFilters);

        //this.fetchReports();
    },

    mounted() {
        this.getAllIndicatorBooks();
    }
}
</script>

<style scoped>
    .table-disabled {
        opacity: 0.5;
    }

    .status-chip {
        min-width: 90px;
    }

    .menu-change-state-color{
        color: hsla(165, 90%, 27%, 1) !important;
    }

    .menu-delete-color{
        color: hsla(1, 45%, 50%, 1) !important;
    }

    .action-buttons {
        display: flex;
        justify-content: flex-end;
        gap: 4px;
        width: 120px;
    }

    .btn-icon {
        width: 36px;
        height: 36px;
        padding: 0;
        display: flex;
        align-items: center;
        justify-content: center;
    }
</style>
